/**
 * A document as a tree (Documents view, next to JSON): keys that open and close, each value's type, arrays and objects
 * with their counts; a scalar edits in place (click it, Enter to keep, Escape to leave) and the change goes into the
 * document's JSON, saved with "Save document" like any other edit. Extended JSON wrappers ({ $oid }, { $date }…) show
 * as one value; a date edits as text, an ObjectId and `_id` do not edit.
 */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { ejsonLeaf, parseScalarEdit, setAtPath, typeOf } from '../../utils/dataImport';

const isBranch = (value) => value !== null && typeof value === 'object' && !ejsonLeaf(value);
const countOf = (value) => (Array.isArray(value) ? `${value.length} item${value.length === 1 ? '' : 's'}` : `${Object.keys(value).length} key${Object.keys(value).length === 1 ? '' : 's'}`);
const shownScalar = (value) => {
  const leaf = ejsonLeaf(value);
  if (leaf) { return leaf.type === 'ObjectId' ? `ObjectId("${leaf.text}")` : `${leaf.type}(${leaf.text})`; }
  if (typeof value === 'string') { return JSON.stringify(value); }
  return String(value);
};
const editStart = (value) => {
  const leaf = ejsonLeaf(value);
  if (leaf) { return leaf.text; }
  return value === null ? 'null' : String(value);
};
const VALUE_CLASS = { string: 'is-s', number: 'is-n', boolean: 'is-n', null: 'is-v' };

function ScalarEditor({ value, label, onDone }) {
  const [text, setText] = useState(editStart(value));
  const [error, setError] = useState(null);
  const done = useRef(false);
  const finish = (next) => {
    if (done.current) { return; }
    done.current = true;
    onDone(next);
  };
  const commit = () => {
    const out = parseScalarEdit(text, value);
    if (!out.ok) { setError(out.error); return; }
    finish(out.value);
  };
  return (
    <span className="data-tree-edit">
      <input
        aria-invalid={error ? 'true' : undefined}
        aria-label={label}
        autoFocus
        className="data-cell-input data-mono"
        onBlur={() => {
          const out = parseScalarEdit(text, value);
          finish(out.ok ? out.value : undefined);
        }}
        onChange={(e) => { setText(e.target.value); setError(null); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); finish(undefined); }
        }}
        onMouseDown={(e) => e.stopPropagation()}
        value={text}
      />
      {error && <span className="data-error-text" role="alert">{error}</span>}
    </span>
  );
}

ScalarEditor.propTypes = { label: PropTypes.string.isRequired, onDone: PropTypes.func.isRequired, value: PropTypes.any };

function Node({ name, value, path, depth, editable, editing, setEditing, onChange }) {
  const [open, setOpen] = useState(depth < 2);
  const id = JSON.stringify(path);
  const branch = isBranch(value);
  const type = typeOf(value);
  const leaf = ejsonLeaf(value);
  const canEdit = editable && !branch && path[0] !== '_id' && (!leaf || leaf.editable);
  const label = path.join('.') || 'document';
  return (
    <li aria-expanded={branch ? open : undefined} className="data-tree-node" role="treeitem">
      <div className="data-tree-row" style={{ paddingLeft: depth * 16 }}>
        {branch ? (
          <button aria-label={`${open ? 'Close' : 'Open'} ${label}`} className="data-btn is-ghost is-tiny" onClick={() => setOpen(!open)} type="button">
            {open ? <CaretDown aria-hidden size={11} /> : <CaretRight aria-hidden size={11} />}
          </button>
        ) : <span className="data-tree-pad" />}
        <span className="is-k">{name}</span>
        <span className="data-tree-type">{type}</span>
        {branch && <span className="data-muted">{countOf(value)}</span>}
        {!branch && (editing === id ? (
          <ScalarEditor
            label={`Value of ${label}`}
            onDone={(next) => {
              setEditing(null);
              if (next !== undefined) { onChange(path, next); }
            }}
            value={value}
          />
        ) : canEdit ? (
          <button className={`data-tree-value ${VALUE_CLASS[type] || 'is-v'}`} onClick={() => setEditing(id)} title="Click to change" type="button">{shownScalar(value)}</button>
        ) : <span className={`data-tree-value ${VALUE_CLASS[type] || 'is-v'}`}>{shownScalar(value)}</span>)}
      </div>
      {branch && open && (
        <ul className="data-tree-list" role="group">
          {(Array.isArray(value) ? value.map((v, i) => [i, v]) : Object.entries(value)).map(([key, child]) => (
            <Node
              depth={depth + 1}
              editable={editable}
              editing={editing}
              key={key}
              name={Array.isArray(value) ? `[${key}]` : String(key)}
              onChange={onChange}
              path={[...path, key]}
              setEditing={setEditing}
              value={child}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

Node.propTypes = {
  depth: PropTypes.number, editable: PropTypes.bool, editing: PropTypes.string, name: PropTypes.string, onChange: PropTypes.func,
  path: PropTypes.array, setEditing: PropTypes.func, value: PropTypes.any,
};

export default function DocTree({ doc, editable = false, onChange }) {
  const [editing, setEditing] = useState(null);
  const change = (path, value) => onChange(setAtPath(doc, path, value));
  return (
    <ul aria-label="Document tree" className="data-tree data-mono" data-testid="doc-tree" role="tree">
      {Object.entries(doc || {}).map(([key, value]) => (
        <Node depth={0} editable={editable} editing={editing} key={key} name={key} onChange={change} path={[key]} setEditing={setEditing} value={value} />
      ))}
    </ul>
  );
}

DocTree.propTypes = { doc: PropTypes.object, editable: PropTypes.bool, onChange: PropTypes.func };
